/**
 * Shared formatting utilities for consistent display across the application
 */

import { devDateTimeService } from '@/services/devDateTimeService';

/**
 * Format a date for display in cards and UI
 * @param date - Date object or ISO string
 * @param options - Formatting options
 * @returns Formatted date string or null if no date provided
 */
export function formatDate(
  date: Date | string | undefined,
  options: {
    includeYear?: boolean | 'auto'; // 'auto' = only if different from current year
    format?: 'short' | 'long';
  } = {}
): string | null {
  if (!date) return null;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    const { includeYear = 'auto', format = 'short' } = options;
    const currentYear = devDateTimeService.now().getFullYear();
    const showYear = includeYear === true ||
      (includeYear === 'auto' && dateObj.getFullYear() !== currentYear);

    return new Intl.DateTimeFormat('en-US', {
      month: format === 'short' ? 'short' : 'long',
      day: 'numeric',
      year: showYear ? 'numeric' : undefined
    }).format(dateObj);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format a snake_case or kebab-case status string to Title Case
 * @param status - Status string like 'in_progress' or 'not-started'
 * @returns Formatted string like 'In Progress' or 'Not Started'
 */
export function formatStatus(status: string): string {
  return status
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Check if a date is in the past (overdue)
 * @param deadline - Deadline date
 * @param status - Current status (completed items are never overdue)
 * @returns True if overdue
 */
export function isOverdue(deadline: Date | string | undefined, status?: string): boolean {
  if (!deadline) return false;
  if (status === 'completed' || status === 'complete') return false;

  const dateObj = typeof deadline === 'string' ? new Date(deadline) : deadline;
  return dateObj < devDateTimeService.now();
}
